import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  URL_SEARCH_CONDITIONS,
  type PagesToReviewSortColumn,
  type PagesToReviewSortDirection,
  type PagesToReviewUrlSearchCondition,
} from '../api/discoverabilityPagesApi';

export const PAGES_TO_REVIEW_DEFAULT_SORT_BY: PagesToReviewSortColumn = 'ai_crawler_visits';
export const PAGES_TO_REVIEW_DEFAULT_SORT_DIRECTION: PagesToReviewSortDirection = 'desc';
export const DEFAULT_URL_SEARCH_CONDITION: PagesToReviewUrlSearchCondition = 'contains';
export const URL_SEARCH_MIN_LENGTH = 3;

const SORT_COLUMNS: PagesToReviewSortColumn[] = ['url', 'ai_crawler_visits', 'status'];

const FILTER_PARAM_NAMES = [
  'urlSearch',
  'urlSearchCondition',
  'sortBy',
  'sortDirection',
  'statusEquals',
  'statusFrom',
  'statusTo',
] as const;

export type PagesToReviewTableParams = {
  urlSearch: string | undefined;
  /** Defined only while a search is active; defaults to `contains`. */
  urlSearchCondition: PagesToReviewUrlSearchCondition | undefined;
  sortBy: PagesToReviewSortColumn;
  sortDirection: PagesToReviewSortDirection;
  statusEquals: number | undefined;
  statusFrom: number | undefined;
  statusTo: number | undefined;
  hasActiveFilters: boolean;
};

export type PagesToReviewTableChange = {
  sortBy: PagesToReviewSortColumn | undefined;
  sortDirection: PagesToReviewSortDirection | undefined;
  statusEquals: number | undefined;
  statusFrom: number | undefined;
  statusTo: number | undefined;
};

function parseSortBy(value: string | null): PagesToReviewSortColumn {
  const column = SORT_COLUMNS.find((sortColumn) => sortColumn === value);
  return column ?? PAGES_TO_REVIEW_DEFAULT_SORT_BY;
}

function parseSortDirection(value: string | null): PagesToReviewSortDirection {
  return value === 'asc' || value === 'desc' ? value : PAGES_TO_REVIEW_DEFAULT_SORT_DIRECTION;
}

function parseStatusCode(value: string | null): number | undefined {
  if (value === null) return undefined;
  const statusCode = Number(value);
  return Number.isInteger(statusCode) ? statusCode : undefined;
}

function parseUrlSearch(value: string | null): string | undefined {
  if (value === null) return undefined;
  const trimmed = value.trim();
  return trimmed.length >= URL_SEARCH_MIN_LENGTH ? trimmed : undefined;
}

function parseUrlSearchCondition(value: string | null): PagesToReviewUrlSearchCondition | undefined {
  return URL_SEARCH_CONDITIONS.find((condition) => condition === value);
}

/**
 * Holds the Pages to Review table state (URL search, sort, status filter) in
 * the page URL search params, consistent with the existing `tab`/`page`
 * params. Every state change resets `page` to 1 so the user never lands on an
 * out-of-range page of the new result set.
 */
export function usePagesToReviewTableParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSearch = parseUrlSearch(searchParams.get('urlSearch'));
  const statusEquals = parseStatusCode(searchParams.get('statusEquals'));
  const statusFrom = parseStatusCode(searchParams.get('statusFrom'));
  const statusTo = parseStatusCode(searchParams.get('statusTo'));

  const tableParams: PagesToReviewTableParams = {
    urlSearch,
    urlSearchCondition:
      urlSearch === undefined
        ? undefined
        : parseUrlSearchCondition(searchParams.get('urlSearchCondition')) ?? DEFAULT_URL_SEARCH_CONDITION,
    sortBy: parseSortBy(searchParams.get('sortBy')),
    sortDirection: parseSortDirection(searchParams.get('sortDirection')),
    statusEquals,
    statusFrom,
    statusTo,
    hasActiveFilters:
      urlSearch !== undefined || statusEquals !== undefined || statusFrom !== undefined || statusTo !== undefined,
  };

  const setUrlSearch = useCallback(
    (value: string | undefined, condition: PagesToReviewUrlSearchCondition = DEFAULT_URL_SEARCH_CONDITION) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        if (value === undefined) {
          next.delete('urlSearch');
          next.delete('urlSearchCondition');
        } else {
          next.set('urlSearch', value);
          next.set('urlSearchCondition', condition);
        }
        next.set('page', '1');
        return next;
      });
    },
    [setSearchParams]
  );

  const applyTableChange = useCallback(
    (change: PagesToReviewTableChange) => {
      // Equals and Between are mutually exclusive — an exact status code wins.
      const statusRangeAllowed = change.statusEquals === undefined;
      const nextValues: Record<string, string | undefined> = {
        sortBy: change.sortBy,
        sortDirection: change.sortDirection,
        statusEquals: change.statusEquals?.toString(),
        statusFrom: statusRangeAllowed ? change.statusFrom?.toString() : undefined,
        statusTo: statusRangeAllowed ? change.statusTo?.toString() : undefined,
      };
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        Object.entries(nextValues).forEach(([name, value]) => {
          if (value === undefined) next.delete(name);
          else next.set(name, value);
        });
        next.set('page', '1');
        return next;
      });
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      FILTER_PARAM_NAMES.forEach((name) => next.delete(name));
      next.set('page', '1');
      return next;
    });
  }, [setSearchParams]);

  return { tableParams, setUrlSearch, applyTableChange, clearFilters };
}
